import { BASE_URL } from '../constants/Env';
import {
  type IPaginationMetaProps,
  type IPaginationResponseData,
  type IResponseData,
  api,
} from './api';
import { type IMatchTemplateColors } from './templates';

export interface ICoursePageDTO {
  id: number;
  cssContent: string | null;
  htmlContent: string;
  pageNumber: number;
  hasWrapper?: boolean;
  title: string;
  createdAt: string;
  updatedAt: string;
  idExternal?: string | null;
  components?: string | null;
  templateColorsFound?: IMatchTemplateColors | null;
}

export interface ICoursePageListDTO {
  id: number;
  title: string;
  pageNumber: number;
}

export type ICoursePageContentId = Pick<ICoursePageDTO, 'id'>;
export type ICoursePageContent = Omit<
  ICoursePageDTO,
  'createdAt' | 'id' | 'updatedAt'
>;

export type IBatchCoursePagesToPatch = Array<
  ICreateCoursePageProps | IUpdateCoursePageProps
>;

export interface ICreateCoursePageProps extends ICoursePageContent {
  courseId: number;
}

export type IDeleteCoursePageProps = ICoursePageContentId;

export interface ICreateCoursePageFromTemplateProps {
  title: string;
  templateId: number;
  courseId: number;
}

export interface IGetCoursePagesByCourseIdProps {
  courseId: number;
}

export interface IGetCoursePageHistoryProps {
  coursePageId: number | string;
  page: number;
  limit: number;
}

export interface IPatchCoursePagesProps {
  courseId: number;
  pages: IBatchCoursePagesToPatch;
}

export interface IPatchCoursePagesComponentsProps {
  courseId: number;
}

export interface ISnapshotCourseVersionFromSavedStateProps {
  courseId: number;
}

export interface ISnapshotCourseVersionFromSavedStateResult {
  courseId: number;
}

export interface ICoursePageHistory {
  data: IHistoryPage[];
  meta: IPaginationMetaProps;
}

export interface IHistoryPage {
  version: number;
  page: Partial<ICoursePageDTO>;
  loggedAt: string;
}

export type IUpdateCoursePageProps = ICoursePageContentId &
  Partial<ICoursePageContent>;

export const createCoursePage = async ({
  ...data
}: ICreateCoursePageProps): Promise<{ message: string }> => {
  const result = await api
    .patch<IResponseData>(`api/course_pages`, data)
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });

  return result;
};

export const createCoursePageFromTemplate = async ({
  title,
  templateId,
  courseId,
}: ICreateCoursePageFromTemplateProps): Promise<ICoursePageDTO> => {
  const result = await api
    .post<IResponseData<ICoursePageDTO>>(
      `api/course_pages/course/${courseId}/template/`,
      {
        title,
        templateId,
      }
    )
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });

  return result;
};

export const deleteCoursePage = async ({
  id,
}: IDeleteCoursePageProps): Promise<{ message: string }> => {
  const result = await api
    .delete<IResponseData>(`api/course_pages/${id}`)
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });

  return result;
};

export async function* getCoursePagesByCourseId({
  courseId,
}: IGetCoursePagesByCourseIdProps): AsyncGenerator<ICoursePageDTO> {
  const token = localStorage.getItem('JWT_TOKEN');

  const url = new URL(`api/course_pages/course/${courseId}`, BASE_URL);

  const response = await fetch(url.href, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.body) {
    return;
  }

  // The backend sends a single JSON object as a stream, not newline-delimited JSON.
  // We must read the entire response body before parsing it.
  const fullBody = await response.text();

  try {
    const result = JSON.parse(fullBody) as IResponseData<ICoursePageDTO[]>;
    if (result.data && Array.isArray(result.data)) {
      for (const page of result.data) {
        yield page;
      }
    }
  } catch (error) {
    console.error('Failed to parse JSON response from stream:', error);
    console.error('Received body:', fullBody);
    throw new Error('Failed to parse streaming response.');
  }
}

export const deleteMultipleCoursePages = async (
  pages: number[],
  id: number
): Promise<{ message: string }> => {
  const result = await api
    .delete<IResponseData>(`api/course_pages/delete_pages/${id}`, {
      data: { pages },
    })
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });

  return result;
};

export const getCoursePageHistory = async ({
  coursePageId,
  page,
  limit,
}: IGetCoursePageHistoryProps): Promise<ICoursePageHistory> => {
  const result = await api
    .get<IPaginationResponseData<IHistoryPage[]>>(
      `api/course_pages/history/${coursePageId}?page=${page}&limit=${limit}`
    )
    .then((resp) => resp.data)
    .catch((err) => {
      throw err;
    });

  return result;
};

export const updateCoursePage = async ({
  id,
  ...data
}: IUpdateCoursePageProps): Promise<{ message: string }> => {
  const result = await api
    .patch<IResponseData>(`api/course_pages/${id}`, data)
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });

  return result;
};

export const patchCoursePages = async ({
  courseId,
  pages,
}: IPatchCoursePagesProps): Promise<ICoursePageDTO[]> => {
  const result = await api
    .patch<IResponseData<ICoursePageDTO[]>>(`api/courses/save/${courseId}`, {
      pages,
    })
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });

  return result;
};

export const snapshotCourseVersionFromSavedState = async ({
  courseId,
}: ISnapshotCourseVersionFromSavedStateProps): Promise<ISnapshotCourseVersionFromSavedStateResult> => {
  const result = await api
    .post<IResponseData<ISnapshotCourseVersionFromSavedStateResult>>(
      `api/courses/save/${courseId}/version`,
      {}
    )
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });

  return result;
};

export const patchCoursePagesComponents = async ({
  courseId,
}: IPatchCoursePagesComponentsProps): Promise<{ message: string }> => {
  const result = await api
    .patch<IResponseData<{ message: string }>>(
      `api/courses/save/${courseId}/components`,
      {}
    )
    .then((resp) => resp.data.data)
    .catch((err) => {
      throw err;
    });

  return result;
};
